import type { Article } from './types'

/**
 * Help for the office setup screens: Pay setup (work types, wage rates,
 * overtime rules), People (employees, new people, crews) and Jobs (jobs,
 * job assignments, job locations), plus one reference article on what is
 * read from Sage and what the portal keeps.
 */
export const OFFICE_SETUP_ARTICLES: Article[] = [
  // ------------------------------------------------------------------ Sage
  {
    slug: 'what-comes-from-sage',
    title: 'What comes from Sage, and what the portal keeps',
    section: 'Office: setup',
    audience: 'office',
    summary:
      'Which information is read live from Sage and cannot be changed here, and which is set up in the portal.',
    body: `The portal sits beside Sage 100. Some information is read straight from Sage every time a screen opens. The rest exists only in the portal, because Sage either cannot hold it or does not have it in a usable form.

## Read from Sage (shown, not changed here)

- **Employees.** Everyone active in Sage, with their employee number, department, **job title**, **location** and badge number. A new hire entered in Sage appears on the [Employees](/office/employees) screen right away, marked **No name**.
- **Jobs.** Open jobs, with description, city, state and the **Prevailing**, **Certified**, **Davis-Bacon** and **Union** flags. A job created in Sage shows up in the portal immediately.
- **The job card wage rate and fringe**, and Sage's manager code for each job.
- **Cost codes and earnings codes.** Work types can only use codes that exist in Sage.
- **Time Track history.** Used for the **Sage 12mo** count on Work types and for **What Sage has actually paid on this job** on Wage rates.

To change any of these, change them in Sage.

## Written back to Sage

The only place the portal changes Sage is the job's **NWL** tab fields, on the [Jobs](/office/jobs) screen and the job detail page, and only fields the portal allows. That includes **Per diem location**. Every change is read back from Sage and logged, so a change Sage did not keep is reported on screen.

## Kept in the portal only

- **Employee names.** Sage encrypts names and addresses, so the portal cannot read them. They are typed in once here.
- **Portal role, PIN, wage classification and residence** for each person.
- **Work types**: the plain-language labels that carry a cost code and an earnings code together.
- **Prevailing wage rates by classification.** These are not copied to the Sage job card, because the card holds one rate and many jobs pay several.
- **Overtime rules**, **crews**, **job assignments**, **job location pins**, and the **per diem rates** sheet.
- **New people** added in the field before Sage has them.

> Changing a name or role in the portal never changes Sage. Changing a job title or department in Sage shows up in the portal on the next load.`,
  },

  // ------------------------------------------------------------- Pay setup
  {
    slug: 'work-types',
    title: 'Work types',
    section: 'Office: setup',
    audience: 'office',
    summary:
      'Plain-language labels that pair a Job Cost code with a Payroll earnings code, and which ones crews can pick.',
    routes: ['/office/work-types'],
    body: `A work type pairs a **cost code** (where the cost lands in Job Cost) with an **earnings code** (how payroll pays it). They are two views of one fact, so they always move together. Supervisors pick a label such as "Travel - Rider" and never see a code. That makes a mismatched pairing impossible rather than something to catch later.

The screen has two lists:

- **Available in the field**: what supervisors can pick on a crew day.
- **Office only**: types accounting applies when correcting a day before export, such as prevailing wage, leave and shop work. Crews never see them.

## Add a work type

1. Click **+ Add work type**.
2. Fill in **Label shown to the field**. Optionally fill in **Name the field sees (optional)** if supervisors call it something shorter. That is wording only; it moves no code.
3. Pick the **Cost code (Job Cost)** and the **Earnings code (Payroll)**. Labor cost codes are listed first.
4. Choose **Recorded as**: **Hours**, or **Dollar amount (posts with zero hours)** for things like per diem and foreman pay.
5. Set **Order in the field list** and tick **Available to supervisors in the field** if crews should see it.
6. Click **Save**.

## Show, hide and delete

- **Hide from field** moves a type to Office only. **Show in field** moves it back. Nothing already recorded changes.
- **Delete** appears only on a type that has never been used. Once any line uses it, hide it instead, so old time still shows what it was.

## Good to know

- **Sage 12mo** is how often that exact code pairing appears in the last year of Sage Time Track history. A pairing with no history is one nobody has used. Give it a second look before showing it to crews.
- Two work types cannot share a label, or share the same cost code and earnings code pair.
- If **Available in the field** is empty, crews cannot record time at all.
- Viewing is open to Office; adding, editing and deleting need an Admin.`,
  },
  {
    slug: 'wage-rates',
    title: 'Prevailing wage rates',
    section: 'Office: setup',
    audience: 'office',
    summary:
      'Enter prevailing wage and Davis-Bacon rates per classification for each job, checked against what Sage has paid.',
    routes: ['/office/wage-rates'],
    body: `A wage determination sets a rate per **classification** (laborer, foreman, operator and so on), not one rate per job. Sage's job card holds a single figure, and most prevailing jobs pay more than one rate. This screen holds the full set.

## The job list

It shows open prevailing jobs. The line at the top counts jobs **with no rates set**, and jobs **where Sage has paid more distinct rates than are configured**. Both are worth working through.

- **Rates set**: how many rates the portal has for the job. **none** means none at all.
- **Rates paid**: how many different rates Sage has paid on the job. Highlighted when it is more than **Rates set**.
- Tick **Only jobs with no rates set** to see the gaps.

Click **Set rates** or **Edit rates** to open a job.

## Add or change a rate

1. On the job, click **+ Add rate**.
2. Pick the **Classification**.
3. Enter **Regular**, and **Fringe** if there is one.
4. Leave **Overtime** and **Double time** blank to use 1.5× and 2× the regular rate. Fill them in when the determination states figures that are not simple multiples.
5. Set **Effective from**, and **To (optional)** when the rate ends.
6. Add a **Notes** entry such as the determination name, then click **Save rate**.

When a determination changes, end the old rate with a **To** date and add a new one, rather than overwriting it. Past days keep the rate that applied then.

## Check against Sage

**What Sage has actually paid on this job** lists each rate paid in Time Track, with **Lines** and **People**. If those disagree with your rates, one of them is wrong. **Use this rate** starts a new rate with that regular figure filled in.

## Rules

- The rates here come first. With none, time falls back to the job card rate, then to the employee's own rate.
- A rate only applies to people with the matching **Wage class** on the [Employees](/help/employees) screen.
- One classification cannot have two rates starting on the same date on one job.
- These rates are not copied to the Sage job card.
- Adding, editing and deleting rates needs an Admin.`,
  },
  {
    slug: 'overtime-rules',
    title: 'Overtime rules',
    section: 'Office: setup',
    audience: 'office',
    summary:
      'The daily and weekly hour limits that flag a week for overtime review on the Problems screen.',
    routes: ['/office/overtime-rules'],
    body: `Overtime rules decide when a week is **flagged for review** on the [Problems](/office/problems) screen. They do not calculate anyone's pay. The flag says someone should look, and you decide what is owed.

This matters because the field records every hour at straight time. The flag is what keeps a long week from going out as a straight-time paycheck.

## The default rule

The row marked **Default** applies to **every state without its own rule**. It is normally 40 hours a week. It cannot be deleted, because every state without a rule depends on it. You can edit it.

## Add a rule for a state

1. Click **Add a state**. It only appears when a state with open jobs has no rule yet.
2. Pick the **State**. The list only offers states with open jobs and no rule.
3. Fill in the limits that apply. Leave a box blank when that state has no such rule:
   - **Overtime over (hours/day)**
   - **Double time over (hours/day)**
   - **Overtime over (hours/week)**
4. Add a **Note** saying what the rule is, and anything it does not cover.
5. Click **Save**.

The Problems screen picks up the change the next time it loads.

## Turning a rule off

Untick **Active** to stop a rule flagging without losing it. The row shows **off**. **Delete** removes it for good, and that state's jobs go back to the default week, so daily overtime stops being flagged there.

## Good to know

- The blue note at the top lists states with open jobs that are using the default week. That is correct for states with no daily overtime law. Add a rule only where one is owed.
- A state cannot be changed on an existing rule. Delete it and add the right one instead.
- **Open jobs** shows how many open jobs each state rule covers.`,
  },

  // ---------------------------------------------------------------- People
  // TODO(verify): office sign-in (email + password) is not set on this
  // screen; confirm where office logins are created before documenting it.
  {
    slug: 'employees',
    title: 'Employees',
    section: 'Office: setup',
    audience: 'office',
    summary:
      'Names, portal roles, wage classifications, residence and field PINs for everyone active in Sage.',
    routes: ['/office/employees'],
    body: `Sage encrypts employee names, so the portal cannot read them. This screen is where names are entered, once, along with the things only the portal holds. Everyone active in Sage is listed. **Job title (from Sage)** and **Location** come straight from Sage, so you can tell who is who before any names exist.

## Find people

- **Department** defaults to **21 — Construction Field Staff**. Pick **All departments** to see everyone.
- **Search** matches number, name, job title or location.
- **Supervisors only** shows people whose job title looks like a supervisor or foreman. They carry a **Supervisor?** badge.
- Click a column heading to sort. People with no name sort last.

## Set someone up

1. Click **Add name** (or **Edit** if they already have one).
2. Enter first and last name. Both are required.
3. Pick a **Role**:
   - **Technician**: crew members. Only Technicians can be put on a crew.
   - **Supervisor**: records crew days and has a crew of their own.
   - **Office** and **Admin**: office screens. Admin can also change setup.
4. Pick the **Wage class**. It decides which prevailing wage rate applies on a job. **not set** means a job's classification rate will not apply to them.
5. Fill in **Residence**: city, two-letter state, zip, and a note such as "over 50 from Kent". Sage cannot supply addresses, and this is what per diem eligibility is judged on.
6. Click **Save**.

## Field sign-in PIN

Field staff sign in on the **Field** tab with their employee number and a PIN. Click **PIN**, type 4 to 12 digits, then **Set**. The **PIN** column then shows **Set**. A forgotten PIN cannot be looked up; set a new one. Setting a PIN also unlocks someone who was locked out after wrong tries.

## Change many at once

Tick the box beside several people, choose **Set wage classification to**, and click **Apply to** (the count). Everyone defaults to Field Laborer, so this is the quick way to move the few foremen or operators.

## Good to know

- **PIN** and the tick box only appear after someone has a name saved.
- People with **No name** cannot be picked for a crew.
- Editing names, roles, classifications and PINs needs an Admin.
- Nothing here changes Sage.`,
  },
  {
    slug: 'new-people',
    title: 'New people from the field',
    section: 'Office: setup',
    audience: 'office',
    summary:
      'Match people a supervisor added in the field to their real Sage employee so their hours can be exported.',
    routes: ['/office/new-people'],
    body: `A new hire often goes straight to a job site and works for days before the paperwork reaches the office and they are entered in Sage. So their hours are not lost, a supervisor can add them in the field as a new person. This screen is where you say who they really are.

**Nothing on this list can be exported to Sage.** Sage never issued these people a number, so their hours wait here until they are matched. Treat it as a to-do list.

## What each row shows

- **Name as entered**, with a temporary number starting with P.
- **What the supervisor said**: any note made when adding them.
- **Added by**: the supervisor to ask if you are not sure who it is.
- **Lines** and **Hours** recorded under the temporary record. Work on the rows with the most hours first; that is someone waiting to be paid.

## Match a person

1. Make sure the person has been entered in Sage, and has a name on the [Employees](/office/employees) screen.
2. In **Who they actually are**, choose **Pick the real employee…** and select them.
3. Click **Match**.
4. Read the confirmation. It names the person, the hours, and how many time entries will move.

Their hours move onto the real employee and then behave like any other hours. The row leaves the list. Tick **Include ones already matched up** to see past matches.

## Rules and "why can't I…"

- **Matching cannot be undone from this screen.** Check the name before confirming.
- You can only match to a real Sage employee, never to another new person.
- If two supervisors added the same new hire, there will be two rows. Match each to the same real employee.
- If the person was also recorded properly on the same day, for the same work, the match is refused and names the day. One of the two lines is wrong. Delete the wrong one on the labor register, then match.
- Matching needs an Admin.`,
  },
  {
    slug: 'crews',
    title: 'Crews',
    section: 'Office: setup',
    audience: 'office',
    summary:
      'Assign field employees to a supervisor so each supervisor picks from their own crew when recording a day.',
    routes: ['/office/crews'],
    body: `A crew is the people who work under a supervisor. When a supervisor records a crew day, they see their own crew instead of the whole company. That makes entry faster. It also limits who a supervisor can record time for, because recording a day is what approves it.

## Before you start

- A supervisor gets a card here once their **Role** is **Supervisor** on the [Employees](/office/employees) screen.
- Only **field employees (F numbers)** can be on a crew. Office, sales and warehouse staff are not offered.
- A crew member needs a name saved and the **Technician** role.

## Build or change a crew

1. Find the supervisor's card and click **Build crew** (or **Edit crew**).
2. Click each person to select them. Selected people show a check mark.
3. Someone marked **on** (name)**'s crew** is already on another crew. Selecting them moves them here. A person belongs to one crew at a time.
4. Click **Save crew**.

## Reading the screen

- The summary line shows how many crews are built, how many field staff are assigned, and how many are **not yet on a crew**.
- **Not on any crew** lists those people. They can still be recorded in the field by any supervisor, as people with no crew, but it is slower for everyone.
- A long list on a card collapses behind **+N more**.

## Good to know

- The supervisor is not listed in their own crew here, but is included automatically when recording time, since they work the day too. So a card may show one fewer than the field does.
- Removing someone ends their place on the crew rather than erasing it. Earlier crew days still show the crew as it was then.
- Changing crews needs an Admin.`,
  },

  // ------------------------------------------------------------------ Jobs
  {
    slug: 'jobs',
    title: 'Jobs and per diem location',
    section: 'Office: setup',
    audience: 'office',
    summary:
      "Check open jobs, set a job's per diem location, and edit the NWL tab fields, which save straight back to Sage.",
    routes: ['/office/jobs'],
    body: `The Jobs screen lists open jobs from Sage with their location and flags (**Prevailing**, **Certified**, **Davis-Bacon**, **Union**). Almost everything is Sage's and is shown for reference. What you can change here saves straight back to Sage.

## Per diem location

Per diem pays by the job's state unless the job names a site with its own rate. For example, a Boise job at Micron pays differently from other Boise jobs.

1. Find the job. **Search job number, description or city** narrows the list.
2. In **Per diem location**, pick the site. The first choice, such as **WA state rate**, clears it.
3. The change saves as soon as you pick.

Only name a location when the state rate is wrong for that site. The list comes from the [Per diem rates](/office/per-diem) sheet, so it never offers a site with no rate.

Tick **Only jobs with a per diem problem** to see jobs that will not pay correctly. The problem shows under the description: **No rate** means nobody would be paid; **Check** means a rate is found but looks wrong.

## Job detail (the NWL tab)

Click a job number to open its detail page. It shows the fields from the **NWL** tab of Job Maintenance in Sage, with Sage's own labels and dropdown lists. Greyed-out fields cannot be changed from the portal.

1. Change the fields you need. Each changed field shows **was** and the old value.
2. Click **Save** (count) **change(s) to Sage**, or **Discard** to drop your changes.

**Portal writes to this job** lists recent changes: **When**, **Field**, **From**, **To**, **Stuck?** and **By**.

## "Sage did not keep the change"

If someone has the job open in Job Maintenance when you save, Sage overwrites your change when they save. The portal reads every change back from Sage. If it did not stick, you get a message, **Stuck?** reads **NO**, and the list shows **last write did not stick**. Ask them to close the job in Sage, then save again.

## Good to know

- Job status, contract amounts and costs are not editable here. Change those in Sage.
- Prevailing wage rates are set on [Wage rates](/office/wage-rates), not on the job card.`,
  },
  {
    slug: 'job-assignments',
    title: 'Job assignments',
    section: 'Office: setup',
    audience: 'office',
    summary:
      'Record who runs each job, and clear the queue of new jobs from Sage that have no project manager.',
    routes: ['/office/job-assignments'],
    body: `This screen records who runs which job. The portal holds it, not Sage. Sage's manager code is free text with no list behind it, and two letters do not identify a person (one code matches two people, another three). A job may have more than one manager.

## The Unassigned queue

New jobs appear in the portal as soon as they are created in Sage, but nobody is assigned to them. They land in **Unassigned**, and stay until someone deals with them. The count turns green at zero, and the list then reads **Every open job has a project manager.**

Each row shows **Job**, **Description**, **State**, **Est. start** and **Sage code**.

1. Click **Assign** on a job.
2. Read the **Sage code** hint. It is a clue, not an answer.
3. Click each person who runs the job. Selected people show a check mark. Beside each name you see their job title, how many jobs they already have, and **cannot sign in** if they have no active portal login.
4. Click **Save 1 person** (or **Save** N **people**).

## Change an assigned job

Under **Change an assigned job**, type a job number or name in **Search**. Click the job, change who is selected, and save. Unselecting everyone changes the button to **Remove everyone from this job**.

## Good to know

- Viewing is open to Office. Only an Admin can change who runs a job; others see **Only an Admin can change who runs a job.**
- People who **cannot sign in** are still offered, because a manager can hold jobs before they have a working login.
- Removing someone ends their assignment rather than erasing it.
- Assignments are groundwork for limiting what each manager sees. They do not limit anything yet.`,
  },
  {
    slug: 'job-locations',
    title: 'Job locations',
    section: 'Office: setup',
    audience: 'office',
    summary:
      "Pin each job's site from crew check-ins or Google Maps so \"I'm on site\" taps can be checked.",
    routes: ['/office/job-locations'],
    body: `When a supervisor taps **I'm on site**, the portal records where the phone was. To say whether that was on site, the job needs a pin: a point and a radius. Until a job is pinned, every reading for it shows **No site pinned**.

Pinning is always done by a person, never automatically. A pin in the wrong place quietly turns honest readings into **Away from site**, so look before you pin.

## Pin from check-ins

**Suggested from check-ins** lists unpinned jobs that have readings. The **Suggested position** is the middle of those readings.

1. Look at **Readings** and **Spread**. Spread is how far the readings sit from each other. A highlighted spread (over 300 m) means people tapped in different places, and the suggestion may be nowhere anyone stood.
2. Click **Look at it** to see the spot in Google Maps.
3. Set **Radius (m)** if needed. It starts at 500. Too tight flags honest crews as away; too loose proves nothing.
4. Click **Pin here**.

## Move a pin

1. In Google Maps, right-click the right spot and click the coordinates to copy them.
2. Under **Pinned**, click **Move** on the job.
3. Paste the coordinates (for example 47.414361, -122.237804) and click **Save**.

To change only the radius, edit **Radius (m)** on the pinned row and click away; it saves.

## Clear a pin

Click **Clear** to remove a pin, such as a test pin in the wrong place. Its readings go back to **No site pinned** and the job returns to the suggestions list if it has readings.

## Pin by hand

For a job nobody has checked in at yet, type the **Job no.** and paste the **latitude, longitude** above the Pinned list, then click **Pin by hand**. It uses a 500 m radius, which you can change afterwards.

## Good to know

- Radius must be between 25 and 20,000 meters.
- **Pinned by** shows who set each pin and how.
- Pinning a job later also makes sense of readings taken before it was pinned.
- A reading is a record, not proof. Phone locations can be wrong or faked.`,
  },
  // --------------------------------------------- /office/supervisor-cutoff
  {
    slug: 'supervisor-cutoff',
    title: 'Supervisor cutoff',
    section: 'Office: setup',
    audience: 'office',
    summary: 'When each pay week closes to supervisor changes, and reopening a week for them.',
    routes: ['/office/supervisor-cutoff'],
    body: `
**Pay setup → Supervisor cutoff** sets a point after which supervisors can no longer change a pay week, so the office has a settled week to check before it goes to Sage.

Without it, a supervisor can change a week until Sage has paid it.

## What closing a week does

Once a week has closed, a supervisor cannot record a crew day in it, correct, move or remove its time, or tick or untick per diem. They see a message telling them to ask the office. On **My crew** the week shows **Closed to supervisors**.

The office is never held to it. You can change the week until it is exported or Sage pays it.

## Setting the deadline (Admin)

1. Tick **Close each week to supervisors** to turn it on.
2. Pick the day in **On the**, for example **Tuesday after the week ends**. The week ends on Saturday, so Tuesday gives Sunday to Tuesday to catch up.
3. Set the time in **At (Pacific)**, for example 11:59 PM.
4. Click **Save**.

It starts **off**. Changing the day or time applies to every week at once, including past ones: a later deadline reopens a week that had closed, and an earlier one closes a week that was open. Only an Admin can change the deadline; the office can see it.

## Reopening a week

Sometimes a supervisor needs to fix a closed week themselves, for example a crew that missed Friday's travel.

1. In **Recent weeks**, click **Reopen…** on the closed week.
2. Set **Until (your time)**. It defaults to 24 hours from now, and can be up to 14 days.
3. Optionally say why.
4. Click **Reopen**.

The week shows **Reopened** with the time, who reopened it and why. It closes again by itself at that time, or click **Close now**.

## Good to know

- Times are Pacific, whatever time zone a supervisor is working in.
- Weeks Sage has already paid stay locked for everyone, whatever this says.
`,
  },
  // -------------------------------------------------- /office/sage-bridge
  {
    slug: 'sage-bridge',
    title: 'Sage bridge',
    section: 'Office: setup',
    audience: 'office',
    summary: 'Whether the Sage bridge is running, and switching writing to Sage on or off.',
    routes: ['/office/sage-bridge'],
    body: `
**Pay setup → Sage bridge** shows the service that writes the portal's time into Sage Time Track. It runs on the Sage server, signs in to Sage as its own user, and writes only lines someone has sent from [Send to Sage](/help/send-to-sage).

## What it shows

For each Sage company, **TST** (test) and **NWL** (live payroll):

- **Bridge running** or **Bridge not running**. The bridge checks in every 15 seconds; if it hasn't checked in for two minutes it shows as not running.
- When it last checked in, from which server, its version, and its last message.
- How many lines are waiting and being written.
- Recent batches and how they ended.

## Switching writing on or off (Admin)

Tick or untick **Write to TST** (or NWL). Off doesn't lose anything: lines sent while it's off wait in the queue until it's switched back on.

**NWL can't be switched on** until the switch-over to live payroll has been approved. Until then the portal refuses it, whatever the box shows.

## If it shows not running

Lines wait safely. Someone with access to the Sage server checks the **NWL Portal Sage bridge** service there. Nothing needs re-sending once it's back.
`,
  },
]
